import { useEffect, useState } from 'react';

// reducers
import { useSelector, useStore } from 'react-redux';
import { selectDistribution, updateDistribution } from 'hyc-store/reducers/distribution';
import { selectCountry } from 'hyc-store/reducers/countries/countriesReducer';
import { SelectWeek } from 'shared/components';

// components
import { SelectYear } from 'shared/components';
import { SelectMonth } from 'shared/components';
import { Select } from 'shared/components/bulma/select';
import { Button } from 'shared/components/bulma/button';
import { DatePicker } from 'shared/components/bulma/date-picker';
import { Modal, ModalContent, ModalControl } from 'shared/components/bulma/modal';

// functions
import { UseToast, ShowErrorToast, ShowSuccessToast } from 'shared/functions';

// helpers
import * as helper from '../helper';

const DEFAULT_MONTHS = [];
for (let index = 0; index < 12; index++) {
    const month = new Date(1, index).toLocaleString('en-GB', { month: 'long' });
    DEFAULT_MONTHS.push(month);
}
const DELIVERY_WEEKS = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];

const ModalShipment = ({ modal, handleModal, payload = null }) => {
    const store = useStore();
    const { month, year, data, partners } = useSelector(selectDistribution);
    const country = useSelector(selectCountry);

    const [info, setInfo] = useState(payload ?? {
        partner: partners ? partners[0] : null,
        week: DELIVERY_WEEKS[0]
    })
    const [returnDate, setReturnDate] = useState(payload?.return_date ?? +new Date());
    const [shipmentDate, setShipmentDate] = useState(payload?.shipment_date ?? +new Date());

    const handleChangeShipment = (event) => {
        setShipmentDate(+event.target.value);
    }

    const handleChangeReturn = (event) => {
        setReturnDate(+event.target.value);
    }

    const handleChange = (event) => {
        const { name, value } = event.target;
        const newInfo = { ...info, [name]: value };
        setInfo(newInfo);
    }

    const handleCreateShipment = async () => {
        info.month = month;
        const created = await helper.createShipment(info, returnDate, shipmentDate, country);

        handleModal(false);

        const weekExist = data?.hasOwnProperty(info.week);
        if (weekExist) {
            const addedWeek = { ...data[info.week] }
            const weekData = {
                ...addedWeek, [info.partner]: { return_date: returnDate, shipment_date: shipmentDate }
            };
            store.dispatch(updateDistribution({ data: { ...data, [info.week]: weekData } }));
        } else {
            store.dispatch(updateDistribution({
                data: { ...data, [info.week]: { [info.partner]: { return_date: returnDate, shipment_date: shipmentDate } } }
            }));
        }


        if (created) {
            ShowSuccessToast('Success!')
        } else {
            ShowErrorToast('Some problem during the shipment date creation!')
        }
    }

    const handleEditShipment = async () => {
        info.month = month;

        const edited = await helper.editShipment(info, returnDate, shipmentDate, country);

        const updatedWeek = { ...data[info.week] };
        const weekData = {
            ...updatedWeek, [info.partner]: { return_date: returnDate, shipment_date: shipmentDate }
        };
        store.dispatch(updateDistribution({ data: { ...data, [info.week]: weekData } }));

        handleModal(false);

        if (edited) {
            ShowSuccessToast('Success!')
        } else {
            ShowErrorToast('Some problem during the shipment date edit!')
        }
    }

    const reset = () => {
        setInfo(payload ?? {
            partner: partners[0],
            week: DELIVERY_WEEKS[0]
        });
        setReturnDate(+new Date());
        setShipmentDate(+new Date());

        handleModal(false);
    }

    const handleYear = (event) => {
        store.dispatch(updateDistribution({ year: event.target.value }));
    }
    const handleMonth = (event) => {
        store.dispatch(updateDistribution({ month: event.target.value }));
    }

    useEffect(() => {
        const getPartners = async (country) => {
            if (!partners) {
                const result = await helper.getPartners(country);
                store.dispatch(updateDistribution({ partners: result }));
                setInfo({ partner: result[0] })
            }
        }
        getPartners(country);

        if (!!payload) {
            setInfo(payload);
        }

        UseToast();
    }, [payload, country, partners, store]);

    if (!partners) { return null; }

    if (!!payload) {

        return (
            <Modal title={`Edit ${month} ${year} - ${info.partner} - ${info.week}`} isActive={modal} handleAccept={handleEditShipment} handleReject={reset}>
                <ModalContent>

                    <div className="columns is-multiline">

                        <div className="column is-6">
                            <DatePicker
                                label="Shipment Date"
                                className="is-250px"
                                id={`shipment-${+new Date()}`}
                                name="shipment-date"
                                value={shipmentDate}
                                onChange={handleChangeShipment}
                            >
                            </DatePicker>
                        </div>

                        <div className="column is-6">
                            <DatePicker
                                label="Date of Return"
                                className="is-250px"
                                id={`return-${+new Date()}`}
                                name="return_date"
                                value={returnDate}
                                onChange={handleChangeReturn}
                            >
                            </DatePicker>
                        </div>

                        <button className="modal-close is-large" aria-label="close" onClick={reset}></button>
                    </div>

                </ModalContent>

                <ModalControl>

                    <Button className="is-primary is-fullwidth" name="submit" onClick={handleEditShipment}>
                        Edit
                    </Button>
                    <Button className=" is-2" name="cancel" onClick={reset}>
                        Cancel
                    </Button>

                </ModalControl>

            </Modal>
        );
    }

    return (
        <Modal title={payload ? "Edit Shipment Dates" : "Create Shipment Dates"} isActive={modal} handleAccept={payload ? handleEditShipment : handleCreateShipment} handleReject={reset}>
            <ModalContent>

                <div className="columns is-multiline">

                    <div className="column is-3">
                        <label className="label is-size-5 mt-3">Month</label>
                        <SelectMonth onChange={handleMonth} month={month} months={DEFAULT_MONTHS} />
                    </div>

                    <div className="column is-3">
                        <label className="label is-size-5 mt-3">Year</label>
                        <SelectYear onChange={handleYear} year={year} />
                    </div>

                    <div className="column is-6">
                        <label className="label is-size-5 mt-3">Distribution partner</label>
                        <Select
                            name="partner"
                            value={info.partner}
                            className="is-fullwidth"
                            options={partners}
                            onChange={handleChange} />
                    </div>

                    <div className="column is-12">
                        <label className="label is-size-5 mt-3">Shipment Week</label>
                        <SelectWeek
                            name="week"
                            value={info.week}
                            onChange={handleChange} />

                    </div>

                    <div className="column is-6">
                        <DatePicker
                            label="Shipment Date"
                            className="is-250px"
                            id="delivery-date"
                            name="shipment-date"
                            value={shipmentDate}
                            onChange={handleChangeShipment}
                        >
                        </DatePicker>
                    </div>

                    <div className="column is-6">
                        <DatePicker
                            label="Date of Return"
                            className="is-250px"
                            id="date-return"
                            name="return_date"
                            value={returnDate}
                            onChange={handleChangeReturn}
                        >
                        </DatePicker>
                    </div>

                    <button className="modal-close is-large" aria-label="close" onClick={reset}></button>
                </div>

            </ModalContent>

            <ModalControl>

                <Button className="is-primary is-fullwidth" name="submit" onClick={handleCreateShipment}>
                    Create new
                </Button>
                <Button className=" is-2" name="cancel" onClick={reset}>
                    Cancel
                </Button>

            </ModalControl>

        </Modal>
    );
}


export { ModalShipment };
